import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import type { FileItem, FileExplorerFilters } from "../../types";
import type { CategoryAttributeResponse } from "@/services/api";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/shared/ui/dropdown-menu";
import { useTags } from "@/features/admin/queries";
import { DEFAULT_TAG_COLOR } from "@/services/api/tags";
import { ChevronDown, X } from "lucide-react";

interface FileExplorerFiltersProps {
  filters: FileExplorerFilters;
  onFiltersChange: (filters: FileExplorerFilters) => void;
  onClearFilters: () => void;
  files: FileItem[];
  categoryAttributes: CategoryAttributeResponse[];
}

export function FileExplorerFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  files,
  categoryAttributes,
}: FileExplorerFiltersProps) {
  const { data: availableTags = [] } = useTags();

  const allTagOptions = useMemo(
    () =>
      availableTags
        .filter((t) => t.isActive && t.name)
        .map((t) => ({
          name: t.name!,
          color: t.color || DEFAULT_TAG_COLOR,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableTags]
  );

  const attributeValuesMap = useMemo(() => {
    const map = new Map<string, string[]>();

    categoryAttributes.forEach((attr) => {
      const attrName = attr.name;
      const values = new Set<string>();

      files.forEach((file) => {
        const attrValue = file.attributeValues?.find(
          (av) => av.attributeName === attrName
        );
        if (attrValue?.value) {
          values.add(attrValue.value);
        }
      });

      map.set(attrName, Array.from(values).sort());
    });

    return map;
  }, [categoryAttributes, files]);

  const updateFilter = useCallback(
    <K extends keyof FileExplorerFilters>(
      key: K,
      value: FileExplorerFilters[K]
    ) => {
      onFiltersChange({ ...filters, [key]: value });
    },
    [filters, onFiltersChange]
  );

  const [nameInput, setNameInput] = useState(filters.name || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setNameInput(filters.name || "");
  }, [filters.name]);

  const handleNameChange = useCallback(
    (value: string) => {
      setNameInput(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateFilter("name", value || undefined);
      }, 400);
    },
    [updateFilter]
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const selectClassName =
    "w-full rounded-md border border-grid-300 px-3 py-2 text-sm focus:border-imperial-500 focus:outline-none focus:ring-1 focus:ring-imperial-500";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Tags
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={`${selectClassName} flex items-center justify-between`}>
                <span className="truncate">
                  {filters.tags.length > 0
                    ? `${filters.tags.length} tag(s) selected`
                    : "All Tags"}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 ml-1" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56 max-h-[300px] overflow-y-auto"
              side="bottom"
              align="start"
              sideOffset={4}
              collisionPadding={8}
            >
              <DropdownMenuLabel>Tags</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allTagOptions.length === 0 ? (
                <DropdownMenuItem disabled>No tags available</DropdownMenuItem>
              ) : (
                allTagOptions.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag.name}
                    checked={filters.tags.includes(tag.name)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        updateFilter("tags", [...filters.tags, tag.name]);
                      } else {
                        updateFilter(
                          "tags",
                          filters.tags.filter((t) => t !== tag.name)
                        );
                      }
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </span>
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {categoryAttributes
          .filter((attr) => {
            const standardFilterKeys = ["name", "tags"];
            const attrNameLower = attr.name.toLowerCase().replace(/\s+/g, "");
            return !standardFilterKeys.includes(attrNameLower);
          })
          .map((attr) => {
            const attrName = attr.name;
            const uniqueValues = attributeValuesMap.get(attrName) || [];
            const options =
              attr.values.length > 0
                ? [...attr.values]
                    .sort((a, b) => a.order - b.order)
                    .map((v) => v.value)
                : uniqueValues;

            const filterValue = filters[attrName];
            const stringValue =
              typeof filterValue === "string" ? filterValue : undefined;

            return (
              <div key={attr.id}>
                <label className="mb-1 block text-xs font-medium text-ink-600">
                  {attr.name}
                </label>
                <select
                  className={selectClassName}
                  value={stringValue || ""}
                  onChange={(e) =>
                    updateFilter(
                      attrName,
                      e.target.value === "" ? undefined : e.target.value
                    )
                  }
                >
                  <option value="">All {attr.name}</option>
                  {options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Name
          </label>
          <Input
            placeholder="Filter by name..."
            value={nameInput}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="flex items-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="gap-1 text-ink-500 hover:text-ink-700"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}
